import { __rest, __awaiter } from '../../node_modules/tslib/tslib.es6.js';
import { queryOptions, useQuery } from '@tanstack/react-query';

var StoreBrandingType;
(function (StoreBrandingType) {
    StoreBrandingType["CustomDTCStore"] = "CustomDTCStore";
    StoreBrandingType["CustomStore"] = "CustomStore";
    StoreBrandingType["BrandStore"] = "BrandStore";
    StoreBrandingType["UniversalStore"] = "UniversalStore";
})(StoreBrandingType || (StoreBrandingType = {}));
// Constants
var CachedQueryKeys;
(function (CachedQueryKeys) {
    CachedQueryKeys["AppConfig"] = "appConfig";
    CachedQueryKeys["AppTheme"] = "appTheme";
    CachedQueryKeys["UserCart"] = "userCart";
    CachedQueryKeys["BrandAppConfigs"] = "brandAppConfigs";
    CachedQueryKeys["BrandClub"] = "brandClub";
    CachedQueryKeys["DTCProduct"] = "dtcProduct";
    CachedQueryKeys["SiteProduct"] = "siteProduct";
    CachedQueryKeys["UserProfile"] = "userProfile";
    CachedQueryKeys["ThemeSetup"] = "themeSetup";
    CachedQueryKeys["SuggestedQueries"] = "suggestedQueries";
})(CachedQueryKeys || (CachedQueryKeys = {}));
const THIRTY_MINUTES_IN_MS = 1000 * 60 * 30;
const FIVE_MINUTES_IN_MS = 1000 * 60 * 5;
// Utility functions
/**
 * Finds the script element that loaded this bundle.
 * Uses multiple strategies to handle deferred scripts and edge cases.
 */
const findScriptElement = () => {
    // Strategy 1: Check for data-api-domain attribute on any script tag
    const scriptsWithDataAttr = Array.from(document.getElementsByTagName('script')).filter((script) => script.hasAttribute('data-api-domain'));
    if (scriptsWithDataAttr.length > 0) {
        return scriptsWithDataAttr[scriptsWithDataAttr.length - 1];
    }
    // Strategy 2: Try document.currentScript (works for non-deferred scripts)
    if (document.currentScript) {
        return document.currentScript;
    }
    // Strategy 3: Search for script tags matching the bundle name pattern
    const allScripts = Array.from(document.getElementsByTagName('script'));
    const matchingScript = allScripts.find((script) => {
        const src = script.src;
        return src && (src.includes('bc-checkout-shop-app') || src.includes('bc-widgets') || src.includes('common-ui'));
    });
    if (matchingScript) {
        return matchingScript;
    }
    // Strategy 4: Fall back to the last script tag with a src
    const lastScriptWithSrc = [...allScripts].reverse().find((script) => script.src);
    if (lastScriptWithSrc) {
        return lastScriptWithSrc;
    }
    return null;
};
/**
 * Extract the host domain from the script URL or data attribute.
 * Falls back to window.location.origin only as a last resort.
 */
const getHostDomain = () => {
    // For brandclub.com domains, use the current origin
    if (window.location.hostname.includes('brandclub.com')) {
        return window.location.origin;
    }
    const scriptElement = findScriptElement();
    // Check for explicit data-api-domain attribute first
    if (scriptElement === null || scriptElement === void 0 ? void 0 : scriptElement.hasAttribute('data-api-domain')) {
        const apiDomain = scriptElement.getAttribute('data-api-domain');
        if (apiDomain) {
            if (apiDomain.startsWith('http://') || apiDomain.startsWith('https://')) {
                return apiDomain;
            }
            return `https://${apiDomain}`;
        }
    }
    // Extract from script src URL
    const scriptSrc = scriptElement === null || scriptElement === void 0 ? void 0 : scriptElement.src;
    if (scriptSrc) {
        try {
            const scriptURL = new URL(scriptSrc);
            return `https://${scriptURL.hostname}`;
        }
        catch (_a) {
            // URL parsing failed, continue to fallback
        }
    }
    // Last resort: use window.location.origin
    if (typeof window !== 'undefined') {
        console.warn('[common-ui] Could not determine script domain, using window.location.origin as fallback. Consider adding data-api-domain attribute to script tag.');
        return window.location.origin;
    }
    return '';
};
const getCommonRequestHeaders = () => {
    return {
    // "X-Client-AppId": ConfigUtils.getWebAppId(), // TODO: Temporarily do not send this header
    };
};
// API functions
const getAppConfig = (signal) => __awaiter(void 0, void 0, void 0, function* () {
    const DOMAIN = `${getHostDomain()}/api-rewards`;
    const res = yield fetch(DOMAIN + '/config/webApp/getAppConfig', {
        method: 'GET',
        headers: Object.assign({ 'Content-Type': 'application/json' }, getCommonRequestHeaders()),
        signal,
    });
    if (res.ok) {
        return res.json();
    }
    else {
        throw new Error('Error fetching app config');
    }
});
const getBrandAppConfigs = (signal, apiBaseUrl) => __awaiter(void 0, void 0, void 0, function* () {
    // Only call getHostDomain() if apiBaseUrl is not provided - avoids potential errors during React rendering
    const DOMAIN = apiBaseUrl || `${getHostDomain()}/api-rewards`;
    const res = yield fetch(DOMAIN + '/brandapp/brandAppConfig', {
        method: 'GET',
        headers: Object.assign({ 'Content-Type': 'application/json' }, getCommonRequestHeaders()),
        signal,
    });
    if (res.ok) {
        return res.json();
    }
    else {
        throw new Error('Error fetching brand app configs');
    }
});
const getSuggestedQueries = (signal, brandId, apiBaseUrl) => __awaiter(void 0, void 0, void 0, function* () {
    // Only call getHostDomain() if apiBaseUrl is not provided - avoids potential errors during React rendering
    const DOMAIN = apiBaseUrl || `${getHostDomain()}/api-rewards`;
    const url = new URL(DOMAIN + '/concierge/suggestedQueries');
    if (brandId) {
        url.searchParams.set('brandId', brandId.toString());
    }
    const res = yield fetch(url.toString(), {
        method: 'GET',
        headers: Object.assign({ 'Content-Type': 'application/json' }, getCommonRequestHeaders()),
        signal,
    });
    if (res.ok) {
        const data = yield res.json();
        return data;
    }
    else {
        throw new Error('Error fetching suggested queries');
    }
});
// Query options
const brandAppConfigQueryOptions = (apiBaseUrl) => {
    return queryOptions({
        queryKey: [CachedQueryKeys.BrandAppConfigs, apiBaseUrl],
        queryFn: ({ signal }) => getBrandAppConfigs(signal, apiBaseUrl),
        // since config data rarely changes, we can use the cached data for longer and refresh it every 30 minutes
        staleTime: THIRTY_MINUTES_IN_MS,
    });
};
const appConfigQueryOptions = () => {
    return queryOptions({
        queryKey: [CachedQueryKeys.AppConfig],
        queryFn: ({ signal }) => getAppConfig(signal),
        // since config data rarely changes, we can use the cached data for longer and refresh it every 30 minutes
        staleTime: THIRTY_MINUTES_IN_MS,
    });
};
const suggestedQueriesQueryOptions = (brandId, apiBaseUrl) => {
    return queryOptions({
        queryKey: [CachedQueryKeys.SuggestedQueries, brandId, apiBaseUrl],
        queryFn: ({ signal }) => getSuggestedQueries(signal, brandId, apiBaseUrl),
        // Suggested queries can be cached for a shorter time since they might change more frequently
        staleTime: FIVE_MINUTES_IN_MS,
    });
};
// Hooks
const useAppConfig = (args) => {
    return useQuery(Object.assign(Object.assign(Object.assign({}, appConfigQueryOptions()), args), { select: args === null || args === void 0 ? void 0 : args.select }));
};
const useDomainConfig = (args) => {
    const _a = useAppConfig(args), { data: appConfig } = _a, rest = __rest(_a, ["data"]);
    return Object.assign({ data: appConfig === null || appConfig === void 0 ? void 0 : appConfig.domainConfig }, rest);
};
const useBrandAppConfig = (apiBaseUrl, args) => {
    return useQuery(Object.assign(Object.assign(Object.assign({}, brandAppConfigQueryOptions(apiBaseUrl)), args), { select: args === null || args === void 0 ? void 0 : args.select }));
};
const useSuggestedQueries = (brandId, apiBaseUrl, args) => {
    return useQuery(Object.assign(Object.assign(Object.assign({}, suggestedQueriesQueryOptions(brandId, apiBaseUrl)), args), { select: args === null || args === void 0 ? void 0 : args.select }));
};

export { CachedQueryKeys, FIVE_MINUTES_IN_MS, StoreBrandingType, THIRTY_MINUTES_IN_MS, appConfigQueryOptions, brandAppConfigQueryOptions, suggestedQueriesQueryOptions, useAppConfig, useBrandAppConfig, useDomainConfig, useSuggestedQueries };
//# sourceMappingURL=appConfigQueryOptions.js.map
